/* eslint-disable no-console */
import { VIDEO_EVENTS } from '@bedrock/media-engine';
import { throwAutoplayErrorHelper } from '../../utils/mediaEngineErrorHelpers';
import { AUTOPLAY_PREVENTED_ERROR } from '../../constants/errors/details';
import { listen } from '../../utils/events.utils';
import { HEARTBEAT_RECOVERY } from '../../recovery/recovery.constants';
import Engine from '../engine';

const ANTI_STALL_INTERVAL_MS = 500;

export class HTML5Engine extends Engine {
  /**
   * @param {Object} options see Engine.configure, plus:
   * @param {boolean} [options.loadOnStart] reloads the element once the source is set
   * @param {boolean} [options.nudgeOnMetadata] moves the playback off zero once the metadata is loaded
   * @param {boolean} [options.awaitCanPlayThrough] resolves `play` once the element can play through
   */
  configure(options) {
    super.configure({ recovery: HEARTBEAT_RECOVERY, ...options });
  }

  init(element) {
    this.videoElement = element;
    this.setEventListeners();
  }

  start(source) {
    console.log('[HTML5Engine] Source playback requested:', source);

    this._playbackAvailable = false;
    this._isFirstTickFired = false;
    this._isTimeFixed = false;
    this._seekStarted = false;
    this.source = source;

    if (!source || !this.videoElement) {
      this.pause();

      return;
    }

    this.startTime = this.source.startTime || 0;
    const timeParameter = this.startTime ? `#t=${this.startTime}` : '';
    if (this.videoElement) {
      if (typeof this.source === 'string') {
        this.videoElement.src = `${this.source}${timeParameter}`;
      } else {
        this.videoElement.src = `${this.source.reference || this.source.src}${timeParameter}`;
      }
    }

    if (this.options && this.options.loadOnStart) {
      this.videoElement.load();
    }
  }

  play() {
    console.log('[HTML5Engine] Playback requested');

    if (!this.videoElement) {
      console.error('[HTML5Engine] Can not play: no video element');

      return Promise.reject(Error('No video element to play'));
    }

    if (!this.videoElement.src) {
      console.error('[HTML5Engine] Can not play: no source');

      return Promise.reject(Error('No source set to play'));
    }

    try {
      const playback = this.videoElement.play();

      return this.options && this.options.awaitCanPlayThrough ? this._whenCanPlayThrough() : playback;
    } catch (error) {
      if (this.options) {
        this.options.dispatcher(VIDEO_EVENTS.ERROR, {
          error: throwAutoplayErrorHelper({
            type: AUTOPLAY_PREVENTED_ERROR,
            details: 'The user did not interacted with the player',
            fatal: true,
            context: this.name,
          }),
        });
      }

      return Promise.reject(error);
    }
  }

  _whenCanPlayThrough() {
    return new Promise(resolve => {
      let unlisten = null;
      const done = () => {
        unlisten();
        resolve();
      };
      // NOTE: "emptied" is fired once the engine is reset, so the promise never outlives the ad
      unlisten = listen(this.videoElement, { canplaythrough: done, error: done, emptied: done });
    });
  }

  pause() {
    console.log('[HTML5Engine] Pause requested');
    this.stopRecovery();

    if (!this.videoElement) {
      return;
    }

    try {
      this.videoElement.pause();
    } catch (error) {
      // NOTE: some STBs may pause but throw an error, ignoring it
    }
  }

  seek(time) {
    console.log(`[HTML5Engine] Seek to ${time} requested`);

    if (!this.videoElement) {
      return;
    }

    const fastSeekSupported = Boolean(this.videoElement.fastSeek);
    console.log(`[HTML5Engine] Seek requested to ${time} with seek mode ${fastSeekSupported}`);

    this._seekStarted = true;

    if (fastSeekSupported) {
      this.videoElement.fastSeek(time);
    } else {
      this.videoElement.currentTime = time;
    }
  }

  setEventListeners() {
    // NOTE: Ensure listeners clean-up if this method was mistakenly called a few times in a row
    this.removeEventListeners();

    if (!this.videoElement) {
      return;
    }

    this._removeListeners = listen(this.videoElement, {
      canplay: this.onVideoElementCanPlay.bind(this),
      canplaythrough: this.onVideoElementCanPlayThrough.bind(this),
      play: this.onVideoElementPlay.bind(this),
      pause: this.onVideoElementPause.bind(this),
      ended: this.onVideoElementEnded.bind(this),
      error: this.onVideoElementError.bind(this),
      seeked: this.onVideoElementSeeked.bind(this),
      seeking: this.onVideoElementSeeking.bind(this),
      loadstart: this.onVideoElementLoadstart.bind(this),
      timeupdate: this.onVideoElementTimeupdate.bind(this),
      ...(this.options && this.options.nudgeOnMetadata ? { loadedmetadata: () => this.nudge() } : {}),
    });
  }

  /**
   * Moves the playback a bit forward, which unfreezes the picture on some decoders
   */
  nudge() {
    if (this.videoElement) {
      this.videoElement.currentTime += 0.001;
    }
  }

  removeEventListeners() {
    if (this._removeListeners) {
      this._removeListeners();
      this._removeListeners = null;
    }
  }

  getCurrentTime() {
    return this.videoElement ? this.videoElement.currentTime : 0;
  }

  getDuration() {
    return this.videoElement ? this.videoElement.duration : 0;
  }

  setVolume(volume) {
    if (this.videoElement) {
      this.videoElement.volume = volume;
    }
  }

  getVolume() {
    return this.videoElement ? this.videoElement.volume : 0;
  }

  isMuted() {
    return this.videoElement ? this.videoElement.muted : false;
  }

  /**
   * Handles HTMLMediaElement: canplay event
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/canplay_event
   */
  onVideoElementCanPlay() {
    console.log('[HTML5Engine] Can play');

    if (!this.videoElement || !this.options || !this.source) {
      return;
    }

    console.log(`[HTML5Engine] Duration on canplay: ${this.videoElement.duration}`);

    if (!this._playbackAvailable) {
      this._playbackAvailable = true;

      // NOTE: will not work for ads as those doesn't have the start time set
      if (Number.isFinite(this.source.startTime) && this.videoElement.currentTime < this.source.startTime) {
        this.videoElement.currentTime = this.startTime;
      }

      this.options.dispatcher(VIDEO_EVENTS.CAN_PLAY, {
        duration: this.videoElement.duration,
      });
    }
  }

  onVideoElementCanPlayThrough() {
    if (!this.videoElement || !this.source) {
      return;
    }

    const duration = this.getDuration();

    console.log(`[HTML5Engine] Can play through, duration: ${duration}, seek started: ${this._seekStarted}`);

    // NOTE: on Magenta true podcasts duration could be retrieved after this event fired, however sometimes it reports Infinity
    if (!Number.isFinite(duration)) {
      return;
    }

    this.options.dispatcher(VIDEO_EVENTS.CAN_PLAY_THROUGH, {
      duration: this.videoElement.duration,
    });

    if (this._seekStarted) {
      return;
    }

    const diff = Math.abs(this.videoElement.currentTime - this.startTime);
    if (Number.isFinite(this.source.startTime) && diff >= 3 && !this._isTimeFixed) {
      this.videoElement.currentTime = this.source.startTime;
    }

    if (!this._isTimeFixed) {
      console.log('[ads] Invoking play on can play through');
      this.play();
    }
  }

  /**
   * Handles HTMLMediaElement: play event
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play_event
   */
  onVideoElementPlay() {
    if (!this.options) {
      return;
    }
    this.options.dispatcher(VIDEO_EVENTS.PLAYING);
  }

  /**
   * Handles HTMLMediaElement: pause event
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/pause_event
   */
  onVideoElementPause() {
    if (!this.options) {
      return;
    }
    this.options.dispatcher(VIDEO_EVENTS.PAUSE);
  }

  /**
   * @param {event} event - Shaka player BufferingEvent.
   * @listens event
   */
  onVideoElementBuffering(event) {
    console.log('[HTML5Engine] Buffering:', event);

    if (!this.options) {
      return;
    }
    this.options.dispatcher(VIDEO_EVENTS.WAITING, event);
  }

  /**
   * Handles HTMLMediaElement: ended event
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/ended_event
   */
  onVideoElementEnded() {
    if (!this.options) {
      return;
    }
    console.log('[HTML5Engine] Playback ended');
    this.options.dispatcher(VIDEO_EVENTS.ENDED);
  }

  onVideoElementError(event) {
    console.log('[HTML5Engine] Error:', event);

    if (!this.options) {
      return;
    }

    this.options.dispatcher(VIDEO_EVENTS.ERROR, {
      error: {
        message: event && event.target && event.target.error ? event.target.error.message : '',
        fatal: true,
      },
    });
  }

  onVideoElementSeeking() {
    if (!this.options) {
      return;
    }

    this.options.dispatcher(VIDEO_EVENTS.SEEKING, {
      from: this._seekFrom,
      to: this._seekTo,
    });
  }

  onVideoElementSeeked(event) {
    if (!this.options) {
      return;
    }
    this.options.dispatcher(VIDEO_EVENTS.SEEKED, {
      from: this._seekFrom,
      to: Math.round(event.target.currentTime),
    });
  }

  onVideoElementLoadstart(event) {
    console.log('[HTML5Engine] Loading started');
    if (!this.options) {
      return;
    }
    this.options.dispatcher(VIDEO_EVENTS.LOAD_START, event);
  }

  onVideoElementTimeupdate(event) {
    if (!this.options) {
      return;
    }

    const { currentTime } = event.target;
    const shouldJumpToStartTime = Number.isFinite(this.source.startTime);

    if (!this._isFirstTickFired) {
      this._isFirstTickFired = true;

      if (shouldJumpToStartTime) {
        this.videoElement.currentTime = this.startTime;
        this._watchForStall();

        return;
      }
    }

    if (!this._isTimeFixed) {
      const diff = Math.abs(currentTime - this.startTime);

      if (shouldJumpToStartTime && diff >= 3) {
        console.log(`[HTML5Engine] Current time ${currentTime} not aligned with ${this.startTime}, attempting to fix`);

        this.videoElement.currentTime = this.startTime;
        this._watchForStall();

        return;
      }

      if (diff < 3) {
        this._isTimeFixed = true;

        clearInterval(this._antiStallTimer);
      }
    }

    this.options.dispatcher(VIDEO_EVENTS.TIME_UPDATE, { currentTime });
  }

  _watchForStall() {
    clearInterval(this._antiStallTimer);

    if (!this.videoElement) {
      return;
    }

    this._lastTime = this.videoElement.currentTime;
    this._antiStallTimer = setInterval(this._checkForStall.bind(this), ANTI_STALL_INTERVAL_MS);
  }

  _checkForStall() {
    if (!this.videoElement) {
      return;
    }

    if (this._lastTime === this.videoElement.currentTime) {
      this.videoElement.currentTime += 0.1;
    }
  }

  isEnded() {
    return this.videoElement ? this.videoElement.ended : false;
  }

  /**
   * Resets current instance state so it could be re-initialized again
   * NOTE: It's critical to ensure this method ends lifecycle of internal player's components so no memory leaks are left
   */
  reset() {
    console.log('[HTML5Engine] Resetting');

    if (this.videoElement) {
      const video = this.videoElement;

      try {
        video.pause();
        console.log('[HTML5Engine] Removing player source');
        video.removeAttribute('src');
        video.load();
        video.src = '';
        video.srcObject = null;
        if (video.lifecycleCleanup) {
          video.lifecycleCleanup();
        }
      } catch (error) {
        console.error('[HTML5Engine] Can not cleanup video element source');
      }
    }

    this.stopRecovery();
    clearInterval(this._antiStallTimer);

    return Promise.resolve();
  }

  release() {
    console.log('[HTML5Engine] Destroying');

    this.removeEventListeners();
    this.reset();

    if (this.videoElement) {
      try {
        this.videoElement.remove();
      } catch (error) {
        console.error('[HTML5Engine] Can not remove video element');
      }
    }

    this.videoElement = null;
    this.source = null;
    this.options = null;
  }
}

HTML5Engine.PLAYER_NAME = 'Bedrock-HTML5';

HTML5Engine.PLAYER_VERSION = '1.0.0';
